import { isAbortError } from "./isAbortError";

/** Max automatic reconnect attempts after a non-intentional AbortError. */
export const SUPABASE_ABORT_RETRY_ATTEMPTS = 5;

/** Base backoff (ms); attempt i waits `base * i`. */
export const SUPABASE_ABORT_RETRY_BASE_MS = 200;

export function abortRetryDelayMs(attemptIndex: number): number {
  // attemptIndex is 1-based retry number after the first failure
  return SUPABASE_ABORT_RETRY_BASE_MS * Math.max(1, attemptIndex);
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isSignalAborted(signal?: AbortSignal | null): boolean {
  return Boolean(signal?.aborted);
}

/**
 * Fetch wrapper for all Supabase clients.
 *
 * Retries when the browser/GoTrue aborts a request due to a race, but NEVER
 * retries when the caller's AbortSignal is already aborted (React Query
 * unmount / navigation cancel — retrying would fight intentional cancel).
 */
export async function fetchWithAbortRetry(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const baseFetch = globalThis.fetch.bind(globalThis);
  let lastError: unknown;

  for (let attempt = 0; attempt < SUPABASE_ABORT_RETRY_ATTEMPTS; attempt++) {
    if (isSignalAborted(init?.signal)) {
      throw lastError instanceof Error
        ? lastError
        : new DOMException("The user aborted a request.", "AbortError");
    }

    try {
      return await baseFetch(input, init);
    } catch (err) {
      lastError = err;
      const intentional = isSignalAborted(init?.signal);
      const canRetry =
        !intentional &&
        isAbortError(err) &&
        attempt < SUPABASE_ABORT_RETRY_ATTEMPTS - 1;

      if (!canRetry) throw err;

      await sleep(abortRetryDelayMs(attempt + 1));
    }
  }

  throw lastError instanceof Error
    ? lastError
    : new Error("Supabase fetch failed after abort retries");
}

type SupabaseResultLike = { error?: unknown } | null | undefined;

/**
 * Retry a Supabase call that either throws AbortError or returns
 * `{ error: AbortError }` (common PostgREST / GoTrue race shape).
 *
 * Silent by design — callers should not toast/log abort while this remends.
 * Prefer supabasePublic for anonymous reads so auth churn is less likely.
 *
 * On final failure still carrying an abort `error` field, throws that error
 * (so React Query can retry) instead of returning a poisoned `{ data, error }`.
 */
export async function withSupabaseAbortRetry<T>(
  run: () => PromiseLike<T>,
  options?: { attempts?: number },
): Promise<T> {
  const attempts = options?.attempts ?? SUPABASE_ABORT_RETRY_ATTEMPTS;
  let last: T | undefined;
  let lastThrown: unknown;

  for (let attempt = 0; attempt < attempts; attempt++) {
    try {
      last = await run();
      const errField = (last as SupabaseResultLike)?.error;
      if (!errField || !isAbortError(errField)) {
        return last;
      }
      lastThrown = errField;
    } catch (err) {
      lastThrown = err;
      if (!isAbortError(err) || attempt >= attempts - 1) {
        throw err;
      }
      await sleep(abortRetryDelayMs(attempt + 1));
      continue;
    }

    if (attempt >= attempts - 1) {
      throw lastThrown instanceof Error
        ? lastThrown
        : Object.assign(new Error("Request aborted"), {
            name: "AbortError",
            cause: lastThrown,
          });
    }
    await sleep(abortRetryDelayMs(attempt + 1));
  }

  if (last !== undefined) {
    const errField = (last as SupabaseResultLike)?.error;
    if (!errField || !isAbortError(errField)) return last;
  }
  throw lastThrown instanceof Error
    ? lastThrown
    : new Error("Supabase call failed after abort retries");
}
